import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTranslation } from 'react-i18next';
import {
  ACTIVE_BUSINESS_MODULE_KEY,
  BUSINESS_MODULES,
  BusinessModuleKey,
  DEFAULT_MODULE_KEY,
  getModulePlaceholderPath,
  MODULE_KEY_TO_LABEL,
} from '../../constants/businessModules';

const getWarehouseDashboardPath = (role: 'admin' | 'staff' | 'customer' | undefined): string => {
  if (role === 'admin') return '/dashboard/admin';
  if (role === 'staff') return '/dashboard/staff';
  return '/dashboard/customer';
};

const normalizeEnabledModuleKeys = (enabledModules: string[] | undefined): Set<BusinessModuleKey> => {
  const labels = new Set((enabledModules || []).map((item) => String(item || '').trim()));
  const keys = new Set<BusinessModuleKey>();
  const labelAliasToKey: Record<string, BusinessModuleKey> = {
    '海外仓/仓配托管': 'warehouse_3pl',
    '包裹转运集运': 'transit_consolidation',
    '快递物流': 'courier_logistics',
    '商品采购分销代理': 'procurement_distribution',
    '商品采购/分销代理': 'procurement_distribution',
  };

  labels.forEach((label) => {
    const mapped = labelAliasToKey[label];
    if (mapped) keys.add(mapped);
  });

  BUSINESS_MODULES.forEach((item) => {
    if (labels.has(item.label)) {
      keys.add(item.key);
    }
  });
  return keys;
};

const getSavedActiveModule = (): BusinessModuleKey => {
  const raw = String(localStorage.getItem(ACTIVE_BUSINESS_MODULE_KEY) || '').trim() as BusinessModuleKey;
  const valid = BUSINESS_MODULES.some((item) => item.key === raw);
  return valid ? raw : DEFAULT_MODULE_KEY;
};

interface HeaderProps {
  sidebarWidth?: number;
}

const Header: React.FC<HeaderProps> = ({ sidebarWidth = 300 }) => {
  const { user, logout } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeModule, setActiveModule] = React.useState<BusinessModuleKey>(() => getSavedActiveModule());

  const isCustomerMain = user?.role === 'customer' && user?.customer_sub_role === 'main_account';
  const isCustomerSub = user?.role === 'customer' && user?.customer_sub_role === 'sub_account';
  const enabledForMain = React.useMemo(
    () => normalizeEnabledModuleKeys(user?.enabled_business_modules),
    [user?.enabled_business_modules]
  );

  const isModuleAvailable = React.useCallback((key: BusinessModuleKey) => {
    if (!user) return false;
    if (user.role === 'admin' || user.role === 'staff') return true;
    if (isCustomerSub) return key === DEFAULT_MODULE_KEY;
    if (isCustomerMain) return enabledForMain.has(key);
    return key === DEFAULT_MODULE_KEY;
  }, [enabledForMain, isCustomerMain, isCustomerSub, user]);

  const switchModule = React.useCallback((nextKey: BusinessModuleKey) => {
    if (!user || !isModuleAvailable(nextKey)) return;
    localStorage.setItem(ACTIVE_BUSINESS_MODULE_KEY, nextKey);
    setActiveModule(nextKey);
    if (nextKey === DEFAULT_MODULE_KEY) {
      navigate(getWarehouseDashboardPath(user.role), { replace: true });
      return;
    }
    navigate(getModulePlaceholderPath(nextKey), { replace: true });
  }, [isModuleAvailable, navigate, user]);

  React.useEffect(() => {
    if (isCustomerSub && activeModule !== DEFAULT_MODULE_KEY) {
      localStorage.setItem(ACTIVE_BUSINESS_MODULE_KEY, DEFAULT_MODULE_KEY);
      setActiveModule(DEFAULT_MODULE_KEY);
    }
  }, [activeModule, isCustomerSub]);

  React.useEffect(() => {
    setActiveModule(getSavedActiveModule());
  }, [location.pathname]);

  React.useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key !== ACTIVE_BUSINESS_MODULE_KEY) return;
      setActiveModule(getSavedActiveModule());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const handleChangeLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('lang', lang);
  };

  const currentModuleLabel = MODULE_KEY_TO_LABEL[activeModule] || MODULE_KEY_TO_LABEL[DEFAULT_MODULE_KEY];
  const backendTypeLabel = user?.role === 'admin'
    ? '管理员后台'
    : user?.role === 'staff'
      ? '员工后台'
      : '客户后台';
  const currentUserText = String(user?.username || '').trim() || String(user?.email || '').trim() || '-';
  const customerCodeText = String(user?.customer_code || '').trim();
  const moduleSelectOptions = React.useMemo(() => {
    if (isCustomerSub) {
      return BUSINESS_MODULES.filter((item) => item.key === DEFAULT_MODULE_KEY);
    }
    return BUSINESS_MODULES;
  }, [isCustomerSub]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 h-16 flex items-center bg-white border-b">
      <div
        className="h-full flex items-center border-r border-gray-200 px-4"
        style={{ width: sidebarWidth }}
      >
        <Link to="/" className="flex items-center gap-3">
          <img
            src="/anfei-logo.png"
            alt="安飞供应链 Logo"
            className="h-10 w-auto object-contain"
          />
          <div className="flex flex-col items-start leading-tight">
            <div className="text-2xl font-bold tracking-tight text-[#3D92F9]">安飞供应链</div>
            <div className="mt-1 text-sm font-medium text-[#6eaefb]">AnFei Supply Chain</div>
          </div>
        </Link>
      </div>

      <div className="min-w-0 flex-1 flex items-center gap-4 px-6">
        <div className="min-w-0 flex-[1.05]">
          <div className="truncate text-xl font-normal text-gray-800">
            {currentModuleLabel} {backendTypeLabel}
          </div>
        </div>

        <div className="min-w-0 flex-[0.75] flex items-center justify-start">
          <div className="flex items-center gap-3 min-w-0">
            <div className="truncate text-sm text-gray-700" title={currentUserText}>
              {currentUserText}
            </div>
            {isCustomerMain ? (
              <div className="shrink-0 text-sm text-gray-700">
                客户编码: {customerCodeText || '-'}
              </div>
            ) : null}
          </div>
        </div>
      
        <div className="min-w-0 flex-[1.7] flex items-center justify-end whitespace-nowrap">
          <div className="mr-4 flex items-center gap-2 whitespace-nowrap">
            <span className="text-sm text-gray-600">切换服务板块:</span>
            <select
              value={activeModule}
              onChange={(e) => switchModule(e.target.value as BusinessModuleKey)}
              disabled={!user || isCustomerSub}
              className="rounded border border-gray-300 bg-white px-2 py-1 text-sm text-gray-700 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400"
            >
              {moduleSelectOptions.map((item) => {
                const available = isModuleAvailable(item.key);
                const suffix = !available ? '（未开通）' : '';
                return (
                  <option key={item.key} value={item.key} disabled={!available}>
                    {item.label}{suffix}
                  </option>
                );
              })}
            </select>
          </div>
          <Link
            to="/"
            className="rounded border border-blue-300 px-3 py-1 text-sm font-medium text-blue-700 hover:bg-blue-50"
          >
            返回首页
          </Link>
          <button
            type="button"
            onClick={() => logout('/login')}
            className="ml-3 rounded bg-blue-600 px-3 py-1 text-sm font-medium text-white hover:bg-blue-700"
          >
            退出
          </button>
          <div className="ml-4 flex items-center space-x-2 whitespace-nowrap">
            <span className="text-sm text-gray-600">{t('language')}:</span>
            <button
              className={`rounded border px-3 py-1 text-sm transition ${
                i18n.language === 'zh-CN'
                  ? 'border-green-300 bg-green-100 text-green-700'
                  : 'border-gray-300 bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              onClick={() => handleChangeLang('zh-CN')}
            >
              中文
            </button>
            <button
              className={`rounded border px-3 py-1 text-sm transition ${
                i18n.language === 'en-US'
                  ? 'border-green-300 bg-green-100 text-green-700'
                  : 'border-gray-300 bg-gray-100 text-gray-500 hover:bg-gray-200'
              }`}
              onClick={() => handleChangeLang('en-US')}
            >
              English
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header; 